// AP-165 · Sistema de idiomas unificado — 5 locales ativos + persistência
// backend (users.language) e frontend (localStorage fallback).
import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import ptBR from "./pt-BR";
import en from "./en";
import ht from "./ht";
import fr from "./fr";
import es from "./es";
import { api } from "../lib/api";

const LOCALES = {
  "pt-BR": ptBR,
  "en":    en,
  "ht-HT": ht,
  "fr":    fr,
  "es":    es,
};

export const AVAILABLE_LOCALES = [
  { code: "pt-BR", label: "Português (Brasil)", flag: "🇧🇷" },
  { code: "ht-HT", label: "Kreyòl Ayisyen",     flag: "🇭🇹" },
  { code: "fr",    label: "Français",           flag: "🇫🇷" },
  { code: "es",    label: "Español",            flag: "🇪🇸" },
  { code: "en",    label: "English",            flag: "🇺🇸" },
];

const I18nContext = createContext({
  locale: "pt-BR",
  t: (k) => k,
  setLocale: () => {},
  availableLocales: AVAILABLE_LOCALES,
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => localStorage.getItem("jms_locale") || "pt-BR");

  // Hidratação do idioma do perfil do usuário logado (persistência server-side).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!alive) return;
        const lang = data?.language || data?.locale;
        if (lang && LOCALES[lang] && lang !== locale) {
          setLocaleState(lang);
          localStorage.setItem("jms_locale", lang);
        }
      } catch { /* não logado — silent */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("jms_locale", locale);
    try { document.documentElement.lang = locale; } catch { /* ignore */ }
  }, [locale]);

  const setLocale = useCallback(async (code) => {
    if (!LOCALES[code]) return;
    setLocaleState(code);
    // Persiste no backend (não bloqueia a UI).
    try { await api.post("/auth/language", { language: code }); } catch { /* silent */ }
  }, []);

  const t = useMemo(() => {
    const dict = LOCALES[locale] || LOCALES["pt-BR"];
    return (key) => dict[key] || LOCALES["pt-BR"][key] || key;
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, availableLocales: AVAILABLE_LOCALES }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
